<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_OVERDRAFT_FEE')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="overdraftFeeTable"
      :items="overdraftFeeList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Client -->
      <template #cell(computationModel)="data">
        <p class="mb-0">
          {{ data.value }}
        </p>
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="variantColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_OVERDRAFT_FEE')"
              @click="onSelectOverdraftFeeItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectOverdraftFeeItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_OVERDRAFT_FEE' : 'ACTIVATE_OVERDRAFT_FEE')"
              @click="overdraftFeeStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      :ok-disabled="isLoading"
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="overdraftFeeRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Fee Name -->
            <b-col md="6">
              <InputField
                id="fee-name"
                v-model="overdraftFormData.name"
                :label="$t('Fee Name')"
                :placeholder="$t('Enter fee name')"
                type="text"
                :error-name="$t('Fee Name')"
                rules="required"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Fee Name -->

            <!-- Fee Charge Cycle -->
            <b-col md="6">
              <b-form-group>
                <SelectField
                  id="fee-charge"
                  v-model="overdraftFormData.chargeCycle"
                  :label="$t('Fee Charge Cycle')"
                  :placeholder="$t('Select')"
                  :options="chargeCycleOptions"
                  :error-name="$t('Fee Charge Cycle')"
                  rules="required"
                  :reduce="option => option.value"
                  item="label"
                  :readonly="isReadonly"
                />
              </b-form-group>
            </b-col>
            <!-- Fee Charge Cycle -->
          </b-row>

          <b-row class="match-height">

            <!-- Fee Computational Model -->
            <b-col md="6">
              <b-form-group>
                <SelectField
                  id="fee-computation"
                  v-model="overdraftFormData.computationModel"
                  :label="$t('Fee Computational Model')"
                  :placeholder="$t('Select')"
                  :options="feeComputationOptions"
                  :error-name="$t('Fee Computational Model')"
                  rules="required"
                  :reduce="option => option.value"
                  item="label"
                  :readonly="isReadonly"
                  :set-selected="setAmountValidation"
                />
              </b-form-group></b-col>
            <!-- Fee Computational Model -->

            <!-- Amount/Percentage -->
            <b-col md="6">
              <InputNumberField
                id="amount"
                v-model.number="overdraftFormData.amount"
                :label="isPercentageOrAmountLabel"
                :placeholder="placeholderLabel"
                type="number"
                :error-name="isPercentageOrAmountLabel"
                :rules="isPercentageOrAmount"
                :readonly="isReadonly"
                :is-allow-negative-numbers="false"
                onpaste="return false;"
              />

            </b-col>
            <!-- Amount/Percentage -->
          </b-row>

          <b-row class="match-height align-items-center">

            <!-- Apply VAT  -->
            <b-col md="6">
              <b-form-group class="pt-1">
                <b-form-checkbox
                  v-model="overdraftFormData.applyVAT"
                  checked="false"
                  name="apply-VAT-button"
                  switch
                  inline
                  :disabled="isReadonly"
                >
                  {{ $t('Apply VAT?') }}
                </b-form-checkbox>
              </b-form-group>

              <!-- Apply VAT  -->

            </b-col>
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchOverdraftFees"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Code -->
        <b-col md="6">
          <InputField
            id="code"
            v-model="filter.code"
            :label="$t('Code')"
            :placeholder="$t('Enter code')"
            type="text"
          />
        </b-col>
        <!-- Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BDropdown, BDropdownItem, BModal, BFormGroup,
  BForm, BFormCheckbox, BBadge, BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { required, maxValue, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import { toKobo, formatAmount } from '@/helpers/formatAmount'
import InputField from '../../../forms/InputField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { toDateWithYear } from '../../../../helpers/formatDate'
import { feeChargeCycle, feeComputation } from '../../../../helpers/data'
import toNumber from '../../../../helpers/toNumber'
import Button from '../../../components/button/Button.vue'
import { accountPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BBadge,
    BCol,
    BSpinner,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    BFormGroup,
    BForm,
    BFormCheckbox,
    ValidationObserver,
    InputField,
    InputNumberField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'name',
      isReadonly: false,
      required,
      maxValue,
      positive,
      toNumber,
      formatAmount,
      showSpinner: false,
      modalTitle: this.$t('Create New Overdraft Fee'),
      buttonLabel: this.$t('Save'),
      isBusy: false,
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'code', sortable: true, label: 'Code' },
        { key: 'amount', sortable: true, label: 'Amount/Percentage' },
        { key: 'chargeCycle', sortable: true, label: 'Charge Cycle' },
        { key: 'computationModel', sortable: true, label: 'Computation Model' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      isPercentageOrAmount: 'required|positive',
      isPercentageOrAmountLabel: this.$t('Amount'),
      placeholderLabel: this.$t('Enter amount'),
      overdraftFormData: {
        name: '',
        amount: '',
        chargeCycle: '',
        computationModel: '',
        applyVAT: false,
        id: '',
      },
      filter: {
        name: '',
        code: '',
        status: '',
        sortColumn: '',
      },
      // feeComputationOptions: feeComputation,
      // chargeCycleOptions: feeChargeCycle,
      overdraftFeeList: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    chargeCycleOptions() {
      return feeChargeCycle.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    feeComputationOptions() {
      return feeComputation.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },

  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchOverdraftFees()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchOverdraftFees()
    },
  },
  mounted() {
    this.fetchOverdraftFees()
  },
  methods: {
    ...mapActions({
      getOverdraftFee: 'account/getOverdraftFeeList',
      updateOverdraftFee: 'account/updateOverdraftFeeAction',
      createOverdraftFee: 'account/createOverdraftFeeAction',
      updateOverdraftFeeStatus: 'account/updateStatus',
    }),
    setAmountValidation(selectedValue) {
      if (selectedValue === 'Percentage') {
        this.isPercentageOrAmount = 'required|max-value:100|positive'
        this.isPercentageOrAmountLabel = this.$t('Percentage')
        this.placeholderLabel = this.$t('Enter percentage')
      } else {
        this.isPercentageOrAmount = 'required|positive'
        this.isPercentageOrAmountLabel = this.$t('Amount')
        this.placeholderLabel = this.$t('Enter amount')
      }
    },
    onSelectOverdraftFeeItem(items, activity, val) {
      this.overdraftFormData.name = items.name
      this.overdraftFormData.amount = items.amount
      this.overdraftFormData.chargeCycle = items.chargeCycle
      this.overdraftFormData.computationModel = items.computationModel
      this.setAmountValidation(items.computationModel)
      this.overdraftFormData.applyVAT = items.applyVAT
      this.overdraftFormData.code = items.code
      this.overdraftFormData.id = items.id
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Overdraft Fee') : this.$t('Overdraft Fee')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async overdraftFeeStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? 'Deactivate' : 'Activate'
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_OVERDRAFT_FEE : accountPermissions.ACTIVATE_OVERDRAFT_FEE

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateOverdraftFeeStatus({
            statusValues, actionType, routeName: 'OverdraftFee', permission,
          })
          if (response.status === 200) {
            const requestText = this.$t('Your request to')
            const successText = this.$t('Overdraft Fee was successful')
            this.fetchOverdraftFees()
            this.alertSuccess({ text: `${requestText}  ${action} ${successText}` })
            this.showSpinner = false
          }
        } catch (error) {
          const requestText = this.$t('Your request to')
          const failText = this.$t('Overdraft Fee failed. Reason:')
          this.alertError({ text: `${requestText} ${action} ${failText} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSureText = this.$t('Are you sure you want to')
      const overdraftText = this.$t('this Overdraft Fee?')
      this.confirm(updateStatus, { title: '', text: `${areYouSureText} ${action} ${overdraftText}` })
    },
    async overdraftFeeAction() {
      try {
        this.isLoading = true
        const overdraftData = {
          ...this.overdraftFormData,
          amount: this.overdraftFormData.computationModel === 'Flat' ? toKobo(this.overdraftFormData.amount) : this.overdraftFormData.amount,
        }
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createOverdraftFee(overdraftData)
          : await this.updateOverdraftFee(overdraftData)

        const { status } = await response
        if (status === 200) {
          const requestText = this.$t('Your request to')
          const successText = this.$t('Overdraft Fee was successful')
          this.fetchOverdraftFees()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: `${requestText} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.hideModal('modal-lg')
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.overdraftFeeRules.validate().then(async success => {
        if (success) {
          const areYouSureText = this.$t('Are you sure you want to')
          const overdraftText = this.$t('this Overdraft Fee?')
          this.confirm(this.overdraftFeeAction, { text: `${areYouSureText} ${this.buttonLabel} ${overdraftText}` })
        }
      })
    },

    async fetchOverdraftFees() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getOverdraftFee({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_OVERDRAFT_FEE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.overdraftFeeList = data.data.map(item => ({
            ...item,
            amount: item.computationModel === 'Flat' ? formatAmount(item.amount) : item.amount,
          }))
          this.totalCount = data.recordCount
          this.isBusy = false
        } else {
          this.overdraftFeeList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    resetModal() {
      this.overdraftFormData.name = ''
      this.overdraftFormData.amount = ''
      this.overdraftFormData.chargeCycle = ''
      this.overdraftFormData.computationModel = ''
      this.overdraftFormData.applyVAT = ''
      this.overdraftFormData.code = ''
      this.overdraftFormData.id = ''
      this.isLoading = false
      this.isReadonly = false
      this.modalTitle = this.$t('Create Overdraft Fee')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter.name = ''
      this.filter.code = ''
      this.filter.status = ''
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchOverdraftFees()
    },
  },
}
</script>
